<template>
  <section v-description-view="descriptionViewParams" class="seo-content">
    <h1 class="seo-content-title">{{ heading }}</h1>
    <div v-if="description" class="description">{{ description }}</div>

    <ul class="seo-content-info-list">
      <li v-for="item in infoItems" :key="item.icon" class="info-list-item">
        <IconBase :name="item.icon" :size="20" />
        <a
          v-if="item.url"
          class="info-list-item-title info-list-item-title--link"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ item.text }}
        </a>
        <span v-else class="info-list-item-title">{{ item.text }}</span>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useAnalytics } from '@/shared/lib/analytics';
import { IconBase } from '@/shared/ui/iconBase';

import type { AttractionInfo } from '../models/attractionCard';

type InfoItem = {
  icon: string;
  text: string;
  url?: string;
};

const props = defineProps<{
  heading: string;
  description: string;
  info: AttractionInfo;
  sightTitle: string;
  sightAddress: string;
}>();

const { useViewTracker } = useAnalytics();
const vDescriptionView = useViewTracker('sight:description.view');
const descriptionViewParams = computed(() => ({
  sight_title: props.sightTitle,
  sight_address: props.sightAddress,
}));

const infoItems = computed<InfoItem[]>(() => {
  const { address, workingHours, ticketPrice, phone, website } = props.info;

  return [
    address && { icon: 'location-pin', text: address },
    workingHours && { icon: 'schedule', text: workingHours },
    ticketPrice && { icon: 'payment-card', text: ticketPrice },
    phone && { icon: 'phone', text: phone },
    website && { icon: 'globe', text: website, url: website },
  ].filter((item): item is InfoItem => Boolean(item));
});
</script>

<style scoped lang="scss">
.seo-content-title {
  margin-top: 8px;

  @include typography.base-1000-950-semi;

  @include tablet {
    @include typography.base-900-900-semi;
  }

  @include mobile {
    @include typography.base-700-500-semi;
  }
}

.description {
  margin-top: 32px;

  @include typography.base-700-700-regular;

  @include tablet {
    @include typography.base-600-500-regular;
  }

  @include mobile {
    margin-top: 16px;

    @include typography.base-500-400-regular;
  }
}

.seo-content-info-list {
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 16px 24px;
  max-width: 571px;
  padding: 0;
  margin-top: 24px;

  @include mobile {
    grid-template-columns: 1fr;
  }
}

.info-list-item-title {
  margin-left: 8px;
}

.info-list-item {
  display: flex;
  align-items: center;
  list-style-type: none;

  @include typography.base-500-400-regular;

  @include mobile {
    @include typography.base-400-300-regular;
  }
}

.info-list-item-title--link {
  color: inherit;
  text-decoration-color: var(--alias-color-link-underline, rgb(0 0 0 / 20%));
}
</style>
